import { PreviewFrameHost } from "../../preview-frame-host";
import { PreviewGemmaReviewBridge } from "./preview-gemma-review-bridge";
import { PreviewReviewOutcomeNote } from "./preview-review-outcome-note";
import { resolveStudioActiveProjectPlan } from "@/features/project/lib/studio-active-project-plan";
import { useStudioStore } from "@/store/studio-store";
import { useEditorStore } from "@/features/page-editor/store/editor-store";
import { usePropertiesPanelAutoOpen } from "@/features/preview/properties/use-properties-panel-auto-open";
import { PreviewSelectionToggle } from "@/features/preview/selection/preview-selection-toggle";
import { DirtyCountChip } from "@/features/preview/dirty/dirty-count-chip";
import { DiscardAllButton } from "@/features/preview/dirty/discard-all-button";
import { SaveButton } from "@/features/preview/save/save-button";
import { DeviceFrame } from "@/features/page-editor/components/canvas/device-frame";
import { viewportIsPinnedToSource } from "@/features/design-system/viewport/viewport-policy";
import {
  generatedAppViewportBreakpoint,
  resolveGeneratedAppViewportMetadata,
} from "@/features/preview/viewport/generated-app-viewport";
export { resolveProjectSourceViewport } from "@/features/preview/viewport/generated-app-viewport";
import { buildGeneratedArtifactIdentity } from "@/features/preview/provenance/generated-artifact-identity";
import { GeneratedArtifactIdentityChip } from "@/features/preview/provenance/generated-artifact-identity-chip";
import { DeviceToolbar } from "@/features/page-editor/components/toolbar/device-toolbar";
import { BREAKPOINT_VIEWPORTS } from "@/features/page-editor/lib/responsive";
import { useStudioLocale } from "@/i18n";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { SelectionCommandBar } from "../selection-command-bar";
import { Download, Maximize2, Minus, Plus, Printer } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  readProjectHeadlessAuthorityBundle,
  type StudioHeadlessAuthorityBundle,
} from "@/features/agent-workspace/headless-authority-bundle";
import type { WorkingReportKind } from "@/store/ide-v2-slice";
import {
  downloadWorkingReportDocx,
  type WorkingReportDocxModel,
} from "./working-report-docx-export";
import { AskCogoSurfaceButton } from "../components/ask-cogo-surface-button";
import { StudioEditorHost } from "../workspace/studio-editor-host";

/**
 * App surface — IDE v2 inner shell for the generated app rail item.
 * The IDE v2 TopBar owns workspace-level header chrome; this surface keeps only
 * preview-specific controls and the reactive viewport frame boundaries.
 */
export function PreviewSurface() {
  const { strings } = useStudioLocale();
  const ideCopy = strings.ide;
  const zoom = useEditorStore((s) => s.zoom);
  const setZoom = useEditorStore((s) => s.setZoom);
  const activeBreakpoint = useEditorStore((s) => s.activeBreakpoint);
  const setBreakpoint = useEditorStore((s) => s.setBreakpoint);
  const editorApp = useEditorStore((s) => s.app);
  // While the review is capturing, the frame lays out at its viewport and the
  // creator's own stays put.
  const currentProject = useStudioStore((s) => s.currentProject);
  const currentProjectId = useStudioStore((s) => s.currentProjectId);
  const currentPageId = useStudioStore((s) => s.currentPageId);
  const workingView = useStudioStore((s) => s.workingPageView);
  const reportKind = useStudioStore((s) => s.workingReportKind);
  const setWorkingPageView = useStudioStore((s) => s.setWorkingPageView);
  const setWorkingReportKind = useStudioStore((s) => s.setWorkingReportKind);
  const setCurrentPageId = useStudioStore((s) => s.setCurrentPageId);
  const storeProjectPlan = useStudioStore((s) => s.projectPlan);
  const projectMeta = useStudioStore((s) => s.projectMeta);
  const projectSettings = useStudioStore((s) => s.projectSettings);
  const projectSource = useStudioStore((s) => s.projectSource);
  const isDirty = useStudioStore((s) => s.isDirty);
  const hasUncommittedArtifactLineage = useStudioStore((s) => s.hasUncommittedArtifactLineage);
  const saveConflict = useStudioStore((s) => s.saveConflict);
  const previewDeviceChoices = useStudioStore((s) => s.previewDeviceChoices);
  const setActiveRailItem = useStudioStore((s) => s.setActiveRailItem);
  // One shared precedence with every other surface — see
  // features/project/lib/studio-active-project-plan.ts. Previously this report
  // read the manifest copy directly and so could contradict Publish.
  const activeProjectPlan = useMemo(
    () =>
      resolveStudioActiveProjectPlan({
        storePlan: storeProjectPlan,
        projectMeta,
        manifest: (currentProject as { manifest?: unknown } | null)?.manifest,
        projectRecord: currentProject,
      }),
    [currentProject, projectMeta, storeProjectPlan],
  );
  const activeProjectPlanRecord = activeProjectPlan.record;
  const editablePageId = useMemo(
    () => resolveEditablePageId(currentProject, currentPageId, editorApp),
    [currentPageId, currentProject, editorApp],
  );
  const artifactIdentity = useMemo(
    () => buildGeneratedArtifactIdentity({
      projectId: currentProjectId,
      projectSource,
      projectMeta,
      projectSettings,
      projectPlan: activeProjectPlan.plan,
      project: currentProject,
      isDirty,
      hasSaveConflict: saveConflict != null,
      hasUncommittedArtifactLineage,
    }),
    [activeProjectPlan.plan, currentProject, currentProjectId, hasUncommittedArtifactLineage, isDirty, projectMeta, projectSettings, projectSource, saveConflict],
  );
  const sourceViewportMetadata = useMemo(
    () => resolveGeneratedAppViewportMetadata(currentProject, currentPageId),
    [currentPageId, currentProject],
  );
  const generatedBreakpoint = generatedAppViewportBreakpoint(sourceViewportMetadata);
  const userDeviceChoice = currentProjectId
    ? previewDeviceChoices[currentProjectId]
    : undefined;
  // The project decides whether its preview follows the source's viewport.
  // Without a decision this is exactly what it was — pinned — so nothing
  // changes for a project nobody has spoken for. See `viewport-policy.ts` for
  // why the decision lives in the manifest rather than the axis store.
  const sourceViewportBound =
    workingView === "app" &&
    viewportIsPinnedToSource(currentProject, sourceViewportMetadata != null);
  useEffect(() => {
    if (sourceViewportBound && generatedBreakpoint && activeBreakpoint !== generatedBreakpoint) {
      setBreakpoint(generatedBreakpoint);
    }
  }, [activeBreakpoint, generatedBreakpoint, setBreakpoint, sourceViewportBound]);
  const activeGeneratedViewport = sourceViewportMetadata &&
    (sourceViewportBound || userDeviceChoice == null)
    ? sourceViewportMetadata
    : undefined;
  const report = useMemo(
    () => buildWorkingReport(currentProject, reportKind, activeProjectPlanRecord),
    [activeProjectPlanRecord, currentProject, reportKind],
  );
  const reportOption = REPORT_KIND_OPTIONS.find(
    (option) => option.id === reportKind,
  );
  const activeAppViewport = activeGeneratedViewport != null
    ? activeGeneratedViewport
    : BREAKPOINT_VIEWPORTS[activeBreakpoint];
  const previewCanvasRef = useRef<HTMLDivElement>(null);
  const [fitPreview, setFitPreview] = useState(true);
  const fitScale = usePreviewFitScale(
    previewCanvasRef,
    activeAppViewport,
    workingView === "app",
  );
  const previewScale = fitPreview ? fitScale : zoom;
  const selectPreviewScale = useCallback(
    (next: number) => {
      setFitPreview(false);
      setZoom(clampPreviewScale(next));
    },
    [setZoom],
  );
  // `Source` is deliberately still English — it names the frame the imported
  // design bound, is reachable only on the source-viewport path this lane
  // could not drive live, and is registered as debt in
  // `src/i18n/__tests__/ide-surface-localized.test.tsx` rather than
  // translated blind.
  const surfaceSizeLabel =
    workingView === "report"
      ? ideCopy.device.sized(ideCopy.device.a4Portrait, 794, 1123)
      : ideCopy.device.sized(
          activeGeneratedViewport ? "Source" : ideCopy.device[activeBreakpoint],
          activeAppViewport.width,
          activeAppViewport.height,
        );
  const surfaceDetailLabel =
    workingView === "report"
      ? (reportOption?.label ?? report.title)
      : ideCopy.pages.runnableDetail;
  const printReport = () => {
    if (typeof window !== "undefined") window.print();
  };
  const downloadReportDocx = () => {
    downloadWorkingReportDocx(report, reportKind);
  };

  // Phase 048 D-048-03 — show the Select/Interact toggle ONLY on the ABox
  // deeplink path.
  const aboxElementMap = useStudioStore(
    (s) => s.currentProject?.aboxElementMap,
  );

  // Phase 050 D-050-03b — auto-open properties panel
  usePropertiesPanelAutoOpen();

  return (
    <StudioEditorHost
      testId="preview-workspace-host"
      navigatorLabel={
        workingView === "report" ? ideCopy.documentList : ideCopy.appPageList
      }
      navigator={
        <WorkingArtifactRail
          workingView={workingView}
          reportKind={reportKind}
          onReportKindChange={setWorkingReportKind}
        />
      }
    >
      <div className="flex h-full min-h-0 flex-col">
        {/* Gated Selection tools in Preview (ABox path only) */}
        {aboxElementMap != null && (
          <div className="flex items-center gap-1.5 border-b border-border bg-card px-4 py-1.5 text-xs font-medium text-muted-foreground">
            <span>ABox Controls:</span>
            <PreviewSelectionToggle />
            <div className="mx-1 h-3 w-px bg-border" aria-hidden="true" />
            <SelectionCommandBar compact showSummary={false} />
            <div className="mx-1 h-3 w-px bg-border" aria-hidden="true" />
            <DirtyCountChip />
            <DiscardAllButton />
            <SaveButton />
          </div>
        )}

        {/* Canvas region — the dedicated sub-header row (testId
            "preview-page-header") was removed (B1): it existed only to host
            the viewport switcher + a review button, so those now float as
            compact overlay pills above the canvas instead of reserving a
            full-width row. */}
        <div className="relative flex min-h-0 flex-1 flex-col">
          {/* Scaled preview container — aligned with CanvasViewport */}
          {workingView === "report" ? (
            <WorkingReportSurface report={report} />
          ) : (
            <div
              ref={previewCanvasRef}
              className="flex-1 min-h-0 overflow-auto bg-muted/30"
              data-canvas-bg="true"
            >
              <div
                className="inline-flex min-h-full items-start justify-center p-8"
                data-canvas-bg="true"
                style={{ minWidth: "100%" }}
              >
                <DeviceFrame
                  sourceViewport={activeGeneratedViewport}
                  safeAreaInsets={activeGeneratedViewport?.safeArea}
                  renderScale={previewScale}
                >
                  <div
                    className="h-full min-h-0 w-full bg-background"
                    style={{
                      height: "100%",
                      minHeight: 0,
                    }}
                  >
                    <PreviewFrameHost />
                    <PreviewGemmaReviewBridge />
                  </div>
                </DeviceFrame>
              </div>
            </div>
          )}

          {/* Top-right floating pill — review action + report-only export controls. */}
          <div className="pointer-events-none absolute inset-x-3 top-3 z-30 flex justify-end">
            <div
              // Capacity guard (review finding, post-source-surface fix): in
              // report mode this pill can carry up to 4 items (Review
              // preview + detail label + DOCX + Print). It floats absolutely
              // over the canvas with no outer toolbar cell to defer overflow
              // to, so it must own its own overflow handling: flex-nowrap +
              // shrink-0 keep the row itself from wrapping onto a 2nd line
              // and growing past its fixed height, and overflow-x-auto lets
              // any genuine overflow scroll/clip within the pill instead of
              // bleeding over the canvas underneath — the same canary
              // failure class Task 3 fixed in source-surface.tsx, applied
              // here on the horizontal axis. See canary-investigation.md.
              data-testid="preview-review-pill"
              className="pointer-events-auto flex max-w-full flex-nowrap shrink-0 items-center gap-1.5 overflow-x-auto rounded-md border border-border bg-card/95 px-1.5 py-1.5 shadow-lg backdrop-blur"
            >
              <AskCogoSurfaceButton
                label={ideCopy.reviewPreview}
                className="h-7 px-2 text-[11px]"
                chatContextPatch={{
                  surfaceId: "preview",
                  ...(currentPageId ? { pageId: currentPageId } : {}),
                }}
                context={() =>
                  [
                    "Studio preview surface: review the active app/document working page, selected viewport, source frame sizing, and preview-level edits chat can perform.",
                    `View=${workingView}`,
                    currentPageId ? `Page=${currentPageId}` : "Page=none",
                    `Surface=${surfaceSizeLabel}`,
                    workingView === "report"
                      ? `Report=${reportOption?.label ?? report.title}`
                      : null,
                  ]
                    .filter(Boolean)
                    .join("; ")
                }
              />
              <span
                data-testid="working-surface-detail"
                className="hidden max-w-32 truncate text-xs text-muted-foreground xl:inline"
              >
                {surfaceDetailLabel}
              </span>
              {workingView === "report" && (
                <>
                  <button
                    type="button"
                    onClick={downloadReportDocx}
                    data-testid="document-docx-download-button"
                    className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-md border border-border bg-background px-2 text-xs font-medium text-foreground hover:bg-accent"
                  >
                    <Download size={13} />
                    DOCX
                  </button>
                  <button
                    type="button"
                    onClick={printReport}
                    data-testid="document-print-button"
                    className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-md border border-border bg-background px-2 text-xs font-medium text-foreground hover:bg-accent"
                  >
                    <Printer size={13} />
                    {ideCopy.print}
                  </button>
                </>
              )}
            </div>
          </div>

          {workingView === "app" ? (
            <div className="pointer-events-none absolute left-3 top-14 z-30 max-w-[calc(100%-1.5rem)]">
              <GeneratedArtifactIdentityChip
                identity={artifactIdentity}
                className="pointer-events-auto"
                onEdit={editablePageId
                  ? () => {
                      setCurrentPageId(editablePageId);
                      useEditorStore.getState().setActivePage(editablePageId);
                      setActiveRailItem("editor");
                    }
                  : undefined}
              />
            </div>
          ) : null}

          {/* Bottom-center floating pill — viewport switcher (B1). Reclaims
              the vertical space the old dedicated sub-header row used to
              take just to host these few controls. */}
          <div className="pointer-events-none absolute inset-x-4 bottom-4 z-20 flex justify-center">
            <div
              role="toolbar"
              aria-label={ideCopy.viewportSwitcher}
              data-testid="preview-viewport-overlay"
              className="pointer-events-auto flex max-w-full flex-nowrap items-center gap-2 overflow-x-auto rounded-full border border-border bg-card/95 px-2 py-1.5 shadow-lg backdrop-blur"
            >
              <TooltipProvider delayDuration={300}>
                <DeviceToolbar
                  activeView={workingView}
                  pageId={currentPageId}
                  onSelectApp={() => setWorkingPageView("app")}
                  onSelectReport={() => setWorkingPageView("report")}
                  showReport
                />
                {workingView === "app" ? (
                  <PreviewZoomControls
                    scale={previewScale}
                    fit={fitPreview}
                    copy={strings.appMap.canvas}
                    onFit={() => setFitPreview(true)}
                    onZoomOut={() => selectPreviewScale(previewScale - 0.1)}
                    onReset={() => selectPreviewScale(1)}
                    onZoomIn={() => selectPreviewScale(previewScale + 0.1)}
                  />
                ) : null}
              </TooltipProvider>
              <div className="mx-0.5 h-4 w-px bg-border" aria-hidden="true" />
              {/* What the automatic review did with this screen. Silent until one
                  has run; every non-success case says which one it was. */}
              <PreviewReviewOutcomeNote />
              <span
                data-testid="working-surface-size"
                className="truncate text-xs text-muted-foreground"
              >
                {surfaceSizeLabel}
              </span>
            </div>
          </div>
        </div>
      </div>
    </StudioEditorHost>
  );
}

const PREVIEW_MIN_SCALE = 0.25;
const PREVIEW_MAX_SCALE = 2;
const PREVIEW_CANVAS_PADDING = 64;

/**
 * Scale a generated app into the canvas region that actually remains after
 * the page navigator and Chat rail take their space.
 *
 * The calculation is pure so the browser measurement and its focused
 * regression test share one rule. A missing measurement keeps 100% until the
 * first ResizeObserver callback instead of briefly collapsing the preview.
 */
function calculatePreviewFitScale(
  canvas: { width: number; height: number },
  viewport: { width: number; height: number },
): number {
  if (
    canvas.width <= 0 ||
    canvas.height <= 0 ||
    viewport.width <= 0 ||
    viewport.height <= 0
  ) {
    return 1;
  }
  const availableWidth = Math.max(1, canvas.width - PREVIEW_CANVAS_PADDING);
  const availableHeight = Math.max(1, canvas.height - PREVIEW_CANVAS_PADDING);
  return clampPreviewScale(
    Math.min(1, availableWidth / viewport.width, availableHeight / viewport.height),
  );
}

function clampPreviewScale(scale: number): number {
  return Math.round(
    Math.min(PREVIEW_MAX_SCALE, Math.max(PREVIEW_MIN_SCALE, scale)) * 100,
  ) / 100;
}

function usePreviewFitScale(
  canvasRef: { current: HTMLDivElement | null },
  viewport: { width: number; height: number },
  enabled: boolean,
): number {
  const [fitScale, setFitScale] = useState(1);

  useEffect(() => {
    if (!enabled) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const update = () => {
      setFitScale(
        calculatePreviewFitScale(
          { width: canvas.clientWidth, height: canvas.clientHeight },
          viewport,
        ),
      );
    };
    update();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", update);
      return () => window.removeEventListener("resize", update);
    }
    const observer = new ResizeObserver(update);
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [canvasRef, enabled, viewport.height, viewport.width]);

  return fitScale;
}

function PreviewZoomControls({
  scale,
  fit,
  copy,
  onFit,
  onZoomOut,
  onReset,
  onZoomIn,
}: {
  scale: number;
  fit: boolean;
  copy: { zoomIn: string; zoomOut: string; fitView: string };
  onFit: () => void;
  onZoomOut: () => void;
  onReset: () => void;
  onZoomIn: () => void;
}) {
  return (
    <div
      data-testid="preview-zoom-controls"
      className="flex shrink-0 items-center gap-0.5"
    >
      <div className="mx-0.5 h-4 w-px bg-border" aria-hidden="true" />
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-7"
            aria-label={copy.zoomOut}
            onClick={onZoomOut}
          >
            <Minus aria-hidden />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{copy.zoomOut}</TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 min-w-12 px-1.5 text-[11px] tabular-nums"
            aria-label={`${Math.round(scale * 100)}% · 100%`}
            onClick={onReset}
          >
            {Math.round(scale * 100)}%
          </Button>
        </TooltipTrigger>
        <TooltipContent>100%</TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-7"
            aria-label={copy.zoomIn}
            onClick={onZoomIn}
          >
            <Plus aria-hidden />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{copy.zoomIn}</TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant={fit ? "secondary" : "ghost"}
            size="sm"
            className="h-7 px-2 text-[11px]"
            aria-label={copy.fitView}
            aria-pressed={fit}
            onClick={onFit}
          >
            <Maximize2 aria-hidden />
            <span className="hidden xl:inline">{copy.fitView}</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent>{copy.fitView}</TooltipContent>
      </Tooltip>
    </div>
  );
}

const REPORT_KIND_OPTIONS: Array<{
  id: WorkingReportKind;
  label: string;
}> = [
  { id: "spec-ontology", label: "명세 온톨로지 보고서" },
  { id: "gap-test", label: "커버리지 및 테스트 보고서" },
];

interface WorkingReportModel extends WorkingReportDocxModel {
  eyebrow: string;
  title: string;
  status: string;
  description: string;
  sourceFrames: Array<{
    name: string;
    detail: string;
  }>;
  metrics: Array<{
    label: string;
    value: string;
  }>;
  sections: Array<{
    title: string;
    body: string;
    rows: Array<{
      label: string;
      value: string;
    }>;
  }>;
  checks: string[];
}

function WorkingReportSurface({ report }: { report: WorkingReportModel }) {
  return (
    <div
      className="flex-1 min-h-0 overflow-auto bg-muted/30 px-6 py-8"
      data-testid="spec-ontology-report-surface"
    >
      <div
        className="mx-auto min-h-[1123px] w-[794px] max-w-full bg-white text-slate-950 shadow-xl ring-1 ring-border"
        data-testid="spec-ontology-report"
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-10 py-6">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
              {report.eyebrow}
            </p>
            <h1 className="mt-2 text-2xl font-semibold tracking-normal text-slate-950">
              {report.title}
            </h1>
            <p className="mt-2 max-w-[560px] text-sm leading-6 text-slate-600">
              {report.description}
            </p>
          </div>
          <div className="flex flex-col items-end gap-3">
            <span className="rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-700">
              {report.status}
            </span>
          </div>
        </div>

        <div className="px-10 py-7">
          <section className="grid grid-cols-4 gap-3">
            {report.metrics.map((metric) => (
              <div
                key={metric.label}
                className="rounded-lg border border-slate-200 p-3"
              >
                <p className="text-[11px] font-medium text-slate-500">
                  {metric.label}
                </p>
                <p className="mt-1 text-lg font-semibold text-slate-950">
                  {metric.value}
                </p>
              </div>
            ))}
          </section>

          <section className="mt-7">
            <h2 className="text-sm font-semibold text-slate-950">
              Source frames
            </h2>
            <div className="mt-3 space-y-2">
              {report.sourceFrames.map((frame) => (
                <div
                  key={`${frame.name}-${frame.detail}`}
                  className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2"
                >
                  <span className="text-sm font-medium text-slate-800">
                    {frame.name}
                  </span>
                  <span className="text-xs text-slate-500">{frame.detail}</span>
                </div>
              ))}
            </div>
          </section>

          <div className="mt-7 grid gap-5">
            {report.sections.map((section) => (
              <section
                key={section.title}
                className="rounded-xl border border-slate-200 p-4"
              >
                <h2 className="text-sm font-semibold text-slate-950">
                  {section.title}
                </h2>
                <p className="mt-1 text-sm leading-6 text-slate-600">
                  {section.body}
                </p>
                <dl className="mt-4 grid grid-cols-2 gap-x-5 gap-y-3">
                  {section.rows.map((row) => (
                    <div key={`${section.title}-${row.label}`}>
                      <dt className="text-[11px] font-medium text-slate-500">
                        {row.label}
                      </dt>
                      <dd className="mt-1 text-sm font-medium text-slate-800">
                        {row.value}
                      </dd>
                    </div>
                  ))}
                </dl>
              </section>
            ))}
          </div>

          <section className="mt-7 rounded-xl bg-slate-50 p-4">
            <h2 className="text-sm font-semibold text-slate-950">
              사용자 확인 항목
            </h2>
            <ul className="mt-3 space-y-2 text-sm leading-6 text-slate-700">
              {report.checks.map((check) => (
                <li key={check} className="flex gap-2">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-400" />
                  <span>{check}</span>
                </li>
              ))}
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
}

function buildWorkingReport(
  project: unknown,
  kind: WorkingReportKind,
  activeProjectPlan: Record<string, unknown> | null,
): WorkingReportModel {
  if (kind === "gap-test") {
    return buildGapAndTestReport(project);
  }
  return buildSpecOntologyReport(project, activeProjectPlan);
}

function buildSpecOntologyReport(
  project: unknown,
  activeProjectPlan: Record<string, unknown> | null,
): WorkingReportModel {
  const projectRecord = readRecord(project);
  const manifest = readRecord(projectRecord?.manifest);
  const metadata = readRecord(projectRecord?.metadata);
  const authorityBundle = readProjectHeadlessAuthorityBundle(project);
  const sourceConversion = readRecord(manifest?.sourceConversion);
  const figmaImport = readRecord(metadata?.cogoXyzFigmaImport);
  const multiFrameAssembly = readRecord(
    metadata?.cogoXyzFigmaMultiFrameAssembly,
  );
  const sourceAnalysisReport =
    readRecord(metadata?.sourceAnalysisReport) ??
    readRecord(manifest?.sourceAnalysisReport) ??
    readRecord(sourceConversion?.sourceAnalysisReport);
  const designSystem = readRecord(projectRecord?.designSystem);
  // The ONE active plan, resolved by the shared precedence before this report
  // is built. Reading the manifest copy here is what let Preview contradict
  // Publish about the same project.
  const projectPlan = readRecord(activeProjectPlan);
  const designSystemRelease = readRecord(projectPlan?.designSystemRelease);
  const ontology = readRecord(projectRecord?.ontology);
  const pages = Array.isArray(manifest?.pages)
    ? manifest.pages.filter(
        (page): page is Record<string, unknown> => readRecord(page) != null,
      )
    : [];
  const flows = readRecord(projectRecord?.flows);
  const ontologyValidation =
    readRecord(figmaImport?.ontologyValidation) ??
    readRecord(readRecord(metadata?.generationEvidence)?.ontologyValidation);
  const designReadiness =
    readRecord(readRecord(designSystem?.qualityReadiness)) ??
    readRecord(
      readRecord(readRecord(figmaImport?.designTemplateApplication))
        ?.qualityReadiness,
    );
  const referenceParity =
    readRecord(figmaImport?.referenceParity) ??
    readRecord(readRecord(metadata?.referenceParity));
  const sourceFrames = collectReportSourceFrames({
    sourceConversion,
    pages,
    figmaImport,
    sourceAnalysisReport,
  });
  const tokenCount =
    readNumber(sourceAnalysisReport?.tokenCount) ??
    readNumber(figmaImport?.extractedTokenCount) ??
    countRecord(readRecord(designSystem?.tokens)) ??
    countArray(readArray(readRecord(ontology?.figmaTemplate)?.tokenRefs));
  const componentCount =
    readNumber(sourceAnalysisReport?.componentCount) ??
    countArray(readArray(figmaImport?.matchedComponents)) ??
    countArray(readArray(readRecord(designSystem)?.tokenRefs));
  const unsupportedCount =
    readNumber(sourceAnalysisReport?.unsupportedNodeCount) ??
    readNumber(figmaImport?.unsupportedNodeCount);
  const matchedNodes =
    readNumber(sourceAnalysisReport?.matchedNodes) ??
    readNumber(figmaImport?.matchedNodes);
  const nodeCount =
    readNumber(sourceAnalysisReport?.nodeCount) ??
    readNumber(figmaImport?.nodeCount);
  const textNodeCount =
    readNumber(sourceAnalysisReport?.textNodeCount) ??
    readNumber(figmaImport?.textNodeCount);
  const mappingCaseCount =
    readNumber(sourceAnalysisReport?.mappingCaseCount) ??
    readNumber(ontologyValidation?.mappingCaseCount);
  const actionMappingCount = readNumber(
    sourceAnalysisReport?.actionMappingCount ??
      ontologyValidation?.actionMappingCount,
  );
  const dataInputMappingCount = readNumber(
    sourceAnalysisReport?.dataInputMappingCount ??
      ontologyValidation?.dataInputMappingCount,
  );
  const pageCount =
    readNumber(multiFrameAssembly?.pageCount) ??
    readNumber(sourceAnalysisReport?.frameCount) ??
    pages.length ??
    readNumber(sourceConversion?.sourceFrameCount);
  const flowCount = countRecord(flows);
  const readinessReasons = readStringList(
    sourceAnalysisReport?.readinessReasons,
  );
  const title = readString(manifest?.name) ?? "Figma source report";
  const hasSourceAnalysis = Boolean(
    sourceAnalysisReport ||
    figmaImport ||
    multiFrameAssembly ||
    authorityBundle,
  );
  const isWaiting = !hasSourceAnalysis;
  const status = isWaiting
    ? "Figma URL 대기"
    : authorityBundle
      ? headlessAuthorityReportStatus(authorityBundle)
      : (readString(sourceAnalysisReport?.readinessStatus) ??
        readString(designReadiness?.status) ??
        readString(ontologyValidation?.status) ??
        "candidate");
  const sourceDescription = isWaiting
    ? "Figma URL을 채팅에 입력하면 원본 화면을 읽고 명세 온톨로지, 디자인 시스템, page, ActionFlow를 이 문서에 순서대로 정리합니다."
    : authorityBundle
      ? "Headless ontology 세션에서 검증한 source-strict authority bundle을 Studio가 소비하도록 정리했습니다. Studio는 이 기준선을 직접 덮어쓰지 않고, 변경 요청은 source/spec 수정으로 되돌려야 합니다."
      : "Figma 원본에서 읽어낸 디자인 내용과 명세 온톨로지 구축 결과를 사용자가 검토할 수 있도록 A4 세로 문서로 정리했습니다.";
  const reportSourceFrames =
    !isWaiting && sourceFrames.length > 0
      ? sourceFrames
      : authorityBundle
        ? buildAuthoritySourceFrames(authorityBundle, title)
        : [
            {
              name: "Figma source 대기",
              detail: "채팅에 검토할 Figma URL을 입력하세요",
            },
          ];

  return {
    eyebrow: "Coogo source analysis",
    title: `${title} · 명세 온톨로지 보고서`,
    status,
    description: sourceDescription,
    sourceFrames: reportSourceFrames,
    metrics: authorityBundle
      ? buildAuthorityMetrics(authorityBundle)
      : [
          {
            label: "Frames",
            value: isWaiting ? "대기" : formatCount(sourceFrames.length),
          },
          {
            label: "Nodes",
            value: isWaiting ? "대기" : formatCount(nodeCount),
          },
          {
            label: "Tokens",
            value: isWaiting ? "대기" : formatCount(tokenCount),
          },
          {
            label: "Pages",
            value: isWaiting ? "대기" : formatCount(pageCount),
          },
        ],
    sections: [
      ...(authorityBundle
        ? [buildHeadlessAuthoritySection(authorityBundle)]
        : []),
      {
        title: "1. Figma source",
        body: "원본 URL에서 읽은 화면 범위와 모바일/데스크톱 판단 기준입니다.",
        rows: [
          {
            label: "Source type",
            value: readString(sourceConversion?.sourceType) ?? "figma",
          },
          {
            label: "Frame",
            value:
              readString(sourceConversion?.frameName) ??
              readString(figmaImport?.frameName) ??
              "Figma URL 입력 후 표시",
          },
          {
            label: "Viewport",
            value: isWaiting
              ? "대기"
              : reportSourceFrames.map((frame) => frame.detail).join(" / "),
          },
          {
            label: "Figma node",
            value:
              readString(sourceConversion?.figmaNodeId) ??
              readString(figmaImport?.figmaNodeId) ??
              "대기",
          },
        ],
      },
      {
        title: "2. 명세 온톨로지",
        body: "원본 디자인을 Cogo가 이해하는 디자인 내용, mapping case, action/data 후보로 정규화한 결과입니다.",
        rows: [
          { label: "Ontology status", value: status },
          {
            label: "디자인 내용",
            value: formatNodeSummary(nodeCount, textNodeCount),
          },
          {
            label: "Matched nodes",
            value: formatMatchedSummary(matchedNodes, nodeCount),
          },
          { label: "Unsupported", value: formatCount(unsupportedCount) },
          { label: "Mapping cases", value: formatCount(mappingCaseCount) },
          {
            label: "Reference parity",
            value: readString(referenceParity?.status) ?? "not reported",
          },
          {
            label: "App readiness",
            value: status,
          },
          {
            label: "Blocker",
            value: isWaiting ? "대기" : formatReasonSummary(readinessReasons),
          },
          {
            label: "Storage",
            value:
              readString(sourceAnalysisReport?.storageStatus) ?? "not reported",
          },
        ],
      },
      {
        title: "3. Design system",
        body: "Figma에서 추출한 색상, 글자, 컴포넌트 후보를 앱 생성에 재사용할 수 있는 형태로 묶습니다.",
        rows: [
          { label: "Tokens", value: formatCount(tokenCount) },
          { label: "Components", value: formatCount(componentCount) },
          {
            label: "Design system",
            value:
              readString(designSystemRelease?.packageId) ??
              readString(designSystem?.designSystemId) ??
              "source-derived",
          },
          {
            label: "Release",
            value: readString(designSystemRelease?.releaseId) ?? "not bound",
          },
          {
            label: "Snapshot",
            value:
              readString(designSystemRelease?.packageSnapshotHash) ??
              "not bound",
          },
          {
            label: "Readiness",
            value: readString(designReadiness?.status) ?? "candidate",
          },
        ],
      },
      {
        title: "4. Application assembly",
        body: "명세 온톨로지를 사용해 page, ActionFlow, data binding 후보를 만들고 실행 화면에서 검증합니다.",
        rows: [
          { label: "Pages", value: formatCount(pageCount) },
          {
            label: "ActionFlow mappings",
            value: formatCount(actionMappingCount ?? flowCount),
          },
          { label: "Data bindings", value: formatCount(dataInputMappingCount) },
          {
            label: "Default device",
            value: isWaiting
              ? "대기"
              : (readString(sourceConversion?.defaultDevice) ??
                readString(readRecord(manifest?.studio)?.defaultDevice) ??
                "source-derived"),
          },
        ],
      },
    ],
    checks: [
      ...(authorityBundle
        ? [
            "Studio는 headless authority bundle을 소비하며 승인된 Coogo JSON을 조용히 수정하지 않습니다.",
            "KYC source scope에 없는 account-selection UX는 이 application에 섞지 않습니다.",
            "변경 요청은 source/spec modification request로 분류하고 headless gates를 다시 실행합니다.",
            "E2E runtime proof와 video proof가 같은 final surface를 가리키는지 확인합니다.",
          ]
        : [
            "Figma 원본이 모바일 화면이면 앱 working page도 모바일 크기로 열려야 합니다.",
            "명세 온톨로지 보고서에서 디자인 내용, mapping, design system, page/ActionFlow 결과를 먼저 확인합니다.",
            "사용자가 확인하면 승인된 application scope에서 생성 및 수정을 이어갑니다.",
            "출력 가능한 리포트는 이 A4 report view를 기준으로 PDF/문서 export 흐름에 연결합니다.",
          ]),
    ],
  };
}

function buildGapAndTestReport(project: unknown): WorkingReportModel {
  const projectRecord = readRecord(project);
  const manifest = readRecord(projectRecord?.manifest);
  const metadata = readRecord(projectRecord?.metadata);
  const authorityBundle = readProjectHeadlessAuthorityBundle(project);
  const sourceConversion = readRecord(manifest?.sourceConversion);
  const sourceAnalysisReport =
    readRecord(metadata?.sourceAnalysisReport) ??
    readRecord(manifest?.sourceAnalysisReport) ??
    readRecord(sourceConversion?.sourceAnalysisReport);
  const gapTestReport =
    readRecord(metadata?.gapTestReport) ?? readRecord(manifest?.gapTestReport);
  const figmaImport = readRecord(metadata?.cogoXyzFigmaImport);
  const referenceParity =
    readRecord(figmaImport?.referenceParity) ??
    readRecord(readRecord(metadata?.referenceParity));
  const pages = Array.isArray(manifest?.pages)
    ? manifest.pages.filter(
        (page): page is Record<string, unknown> => readRecord(page) != null,
      )
    : [];
  const sourceFrames = collectReportSourceFrames({
    sourceConversion,
    pages,
    figmaImport,
    sourceAnalysisReport,
  });
  const isWaiting = !sourceAnalysisReport && !authorityBundle;
  const title = readString(manifest?.name) ?? "Application";
  const unsupportedCount = readNumber(
    sourceAnalysisReport?.unsupportedNodeCount,
  );
  const nodeCount = readNumber(sourceAnalysisReport?.nodeCount);
  const matchedNodes = readNumber(sourceAnalysisReport?.matchedNodes);
  const storageStatus = readString(sourceAnalysisReport?.storageStatus);
  const readinessStatus = readString(sourceAnalysisReport?.readinessStatus);
  const readinessReasons =
    readStringList(gapTestReport?.readinessReasons) ??
    readStringList(sourceAnalysisReport?.readinessReasons);
  const testStatus = readString(gapTestReport?.testStatus);
  const testRows = readArray(gapTestReport?.tests)
    ?.map((test) => readRecord(test))
    .filter((test): test is Record<string, unknown> => test != null);
  const gapCount =
    readNumber(gapTestReport?.unsupportedNodeCount) ??
    (unsupportedCount !== undefined ? unsupportedCount : undefined);
  const status = isWaiting
    ? "테스트 대기"
    : authorityBundle
      ? headlessAuthorityReportStatus(authorityBundle)
      : (testStatus ?? readinessStatus ?? "candidate");
  const reportSourceFrames =
    !isWaiting && sourceFrames.length > 0
      ? sourceFrames
      : authorityBundle
        ? buildAuthoritySourceFrames(authorityBundle, title)
        : [
            {
              name: "커버리지/테스트 대기",
              detail: "Figma 분석 및 앱 생성 후 결과 생성",
            },
          ];

  return {
    eyebrow: "Coogo coverage and test result",
    title: `${title} · 커버리지 및 테스트 보고서`,
    status,
    description: isWaiting
      ? "사용자가 명세 온톨로지를 확인하고 모바일 working page에서 개발이 끝나면 자동 테스트를 실행하고, 시각 커버리지 이슈와 runtime 결과를 이 보고서에 남깁니다."
      : authorityBundle
        ? "Headless ontology 세션의 assembly, story coverage, overlap audit, runtime E2E, video proof를 Studio에서 확인할 수 있도록 묶었습니다."
        : "Figma source와 생성 application 사이의 커버리지 이슈, ontology 저장/재사용, 자동 테스트 및 실행 검증 결과를 정리했습니다.",
    sourceFrames: reportSourceFrames,
    metrics: authorityBundle
      ? buildAuthorityGapMetrics(authorityBundle)
      : [
          {
            label: "커버리지 이슈",
            value: isWaiting ? "대기" : formatCount(gapCount),
          },
          {
            label: "Coverage",
            value: isWaiting
              ? "대기"
              : formatCoverageSummary(
                  readNumber(gapTestReport?.matchedNodes) ?? matchedNodes,
                  readNumber(gapTestReport?.nodeCount) ?? nodeCount,
                ),
          },
          {
            label: "Storage",
            value: isWaiting ? "대기" : (storageStatus ?? "not reported"),
          },
          {
            label: "Tests",
            value: isWaiting
              ? "대기"
              : testRows
                ? `${testRows.length} checks`
                : "자동 테스트 대기",
          },
        ],
    sections: [
      ...(authorityBundle
        ? [buildHeadlessRuntimeProofSection(authorityBundle)]
        : []),
      {
        title: "1. Source coverage",
        body: "Figma 디자인 내용이 Coogo application 생성에 얼마나 연결되었는지 확인합니다.",
        rows: [
          {
            label: "Matched nodes",
            value: formatMatchedSummary(
              readNumber(gapTestReport?.matchedNodes) ?? matchedNodes,
              readNumber(gapTestReport?.nodeCount) ?? nodeCount,
            ),
          },
          { label: "Unsupported", value: formatCount(unsupportedCount) },
          {
            label: "Coverage",
            value: formatCoverageSummary(
              readNumber(gapTestReport?.matchedNodes) ?? matchedNodes,
              readNumber(gapTestReport?.nodeCount) ?? nodeCount,
            ),
          },
          {
            label: "Storage",
            value: storageStatus ?? "not reported",
          },
        ],
      },
      {
        title: "2. 시각 커버리지 이슈",
        body: "Figma 원본과 생성된 앱 화면이 다르게 보이는 부분을 소스 커버리지 이슈로 추적해야 합니다.",
        rows: [
          {
            label: "Reference parity",
            value: readString(referenceParity?.status) ?? "not reported",
          },
          {
            label: "Mobile size",
            value:
              gapTestReport?.mobileFrameDetected === true
                ? "mobile frame detected"
                : reportSourceFrames.some((frame) =>
                      frame.detail.includes("mobile"),
                    )
                  ? "mobile frame detected"
                  : "확인 필요",
          },
          {
            label: "확인된 이슈",
            value: isWaiting ? "대기" : formatReasonSummary(readinessReasons),
          },
          {
            label: "Next",
            value: "chat correction 후 재검증",
          },
        ],
      },
      {
        title: "3. Runtime test",
        body: "개발 완료 후 application을 자동 실행하고, 사용자가 확인해야 할 테스트 결과를 기록합니다.",
        rows: [
          { label: "App page", value: pages.length > 0 ? "열림" : "대기" },
          {
            label: "ActionFlow",
            value: formatTestDetail(testRows, "source coverage"),
          },
          {
            label: "Compiler",
            value: formatTestDetail(testRows, "compiler readiness"),
          },
          {
            label: "Data",
            value: formatTestDetail(testRows, "application pages"),
          },
          { label: "Report", value: "A4 result report" },
        ],
      },
      {
        title: "4. 해결 기준",
        body: "커버리지 이슈가 확인되면 사용자가 chat으로 확인한 뒤 디자인 내용, mapping, renderer, ActionFlow 중 하나로 분류하고 수정합니다.",
        rows: [
          { label: "디자인 내용", value: "누락 여부 확인" },
          { label: "Mapping", value: "component/rule 보정" },
          { label: "Renderer", value: "크기/간격/타이포 확인" },
          { label: "ActionFlow", value: "사용자 시나리오 확인" },
        ],
      },
    ],
    checks: [
      ...(authorityBundle
        ? [
            "Application assembly, story coverage, overlap audit, app play video proof가 모두 같은 source-strict 범위를 따라야 합니다.",
            "source requirement gap이 있으면 Studio에서 UI를 invent하지 않고 gap으로 노출합니다.",
            "account-selection UX는 KYC application scope에서 제외된 상태를 유지합니다.",
            "사용자 변경은 source/spec 수정 요청으로 되돌리고 headless 기준선을 재생성합니다.",
          ]
        : [
            "모바일 source가 데스크탑 크기로 생성되면 소스 커버리지 이슈로 기록합니다.",
            "Figma와 다른 색상, 간격, 글자 크기, 화면 구조를 시각 커버리지 이슈로 분리합니다.",
            "개발 완료 후 자동 테스트를 실행하고, 결과는 App 실행 화면과 Report 문서 양쪽에서 확인 가능해야 합니다.",
            "커버리지 및 테스트 보고서는 다음 수정 loop의 입력으로 사용합니다.",
          ]),
    ],
  };
}

function headlessAuthorityReportStatus(
  bundle: StudioHeadlessAuthorityBundle,
): string {
  return (
    bundle.status ||
    bundle.application?.readiness ||
    bundle.runtimeE2E?.status ||
    bundle.videoProof?.status ||
    "unknown"
  );
}

function formatStatusDetail(
  status: string | undefined,
  detail: string | undefined,
): string {
  const parts = [status, detail].filter(
    (part): part is string => typeof part === "string" && part.length > 0,
  );
  return parts.length > 0 ? parts.join(" · ") : "not reported";
}

function formatPolicyFlag(value: boolean | undefined): string {
  if (value === true) return "allowed";
  if (value === false) return "blocked";
  return "not declared";
}

function formatAccountUxBoundary(value: boolean | string | undefined): string {
  if (value === true) return "pass";
  if (value === false) return "fail";
  return value ?? "not reported";
}

function formatAuthorityGapCount(
  bundle: StudioHeadlessAuthorityBundle,
): string {
  const values = [
    bundle.application?.rawFigmaExtractionGapCount,
    bundle.application?.sourceCoverageUnassembledFactCount,
    bundle.application?.sourceAuthorityViolationCount,
  ].filter((value): value is number => typeof value === "number");
  if (values.length === 0) return "not reported";
  return String(values.reduce((sum, value) => sum + value, 0));
}

function buildAuthoritySourceFrames(
  bundle: StudioHeadlessAuthorityBundle,
  fallbackTitle: string,
): Array<{ name: string; detail: string }> {
  return [
    {
      name: bundle.sourceDocumentProfile?.rootName ?? fallbackTitle,
      detail: formatStatusDetail(
        bundle.sourceDocumentProfile?.profileKind,
        bundle.sourceDocumentProfile?.sourceComplianceMode ??
          bundle.policy?.sourceComplianceMode,
      ),
    },
  ];
}

function buildAuthorityMetrics(
  bundle: StudioHeadlessAuthorityBundle,
): WorkingReportModel["metrics"] {
  return [
    {
      label: "Source profile",
      value: bundle.sourceDocumentProfile?.status ?? bundle.status,
    },
    {
      label: "Story anchors",
      value: formatCount(bundle.sourceDocumentProfile?.storyAnchorCount),
    },
    {
      label: "Surfaces",
      value: formatCount(bundle.application?.surfaceCount),
    },
    {
      label: "Video proof",
      value: bundle.videoProof?.status ?? "not reported",
    },
  ];
}

function buildAuthorityGapMetrics(
  bundle: StudioHeadlessAuthorityBundle,
): WorkingReportModel["metrics"] {
  return [
    {
      label: "Authority gaps",
      value: formatAuthorityGapCount(bundle),
    },
    {
      label: "Runtime E2E",
      value: bundle.runtimeE2E?.status ?? "not reported",
    },
    {
      label: "Video proof",
      value: bundle.videoProof?.status ?? "not reported",
    },
    {
      label: "Final surface",
      value:
        bundle.runtimeE2E?.finalSurfaceRef ??
        bundle.videoProof?.finalSurfaceRef ??
        "not reported",
    },
  ];
}

function buildHeadlessAuthoritySection(
  bundle: StudioHeadlessAuthorityBundle,
): WorkingReportModel["sections"][number] {
  return {
    title: "0. Headless authority bundle",
    body: "Ontology/headless 세션에서 생성한 source-strict 기준선입니다. Studio는 이 기준선을 소비하고, UI를 임의로 보강하거나 조용히 Coogo JSON을 수정하지 않습니다.",
    rows: [
      {
        label: "Source profile",
        value: formatStatusDetail(
          bundle.sourceDocumentProfile?.status,
          bundle.sourceDocumentProfile?.profileKind,
        ),
      },
      {
        label: "Source policy",
        value:
          bundle.specFlowOntology?.sourceComplianceMode ??
          bundle.sourceDocumentProfile?.sourceComplianceMode ??
          bundle.policy?.sourceComplianceMode ??
          "not reported",
      },
      {
        label: "Spec-flow ontology",
        value: formatStatusDetail(
          bundle.specFlowOntology?.status,
          bundle.specFlowOntology?.ontologyVersion,
        ),
      },
      {
        label: "Application",
        value: formatStatusDetail(
          bundle.application?.status,
          bundle.application?.readiness,
        ),
      },
      {
        label: "Runtime proof",
        value: formatStatusDetail(
          bundle.runtimeE2E?.status,
          bundle.runtimeE2E?.finalSurfaceRef,
        ),
      },
      {
        label: "Silent JSON mutation",
        value: formatPolicyFlag(bundle.policy?.studioMaySilentlyMutateCogoJson),
      },
      {
        label: "Model JSON patch",
        value: formatPolicyFlag(bundle.policy?.modelMayPatchCogoJsonDirectly),
      },
      {
        label: "Change route",
        value:
          bundle.policy?.changeRoute ??
          "source/spec modification request and rerun headless gates",
      },
    ],
  };
}

function buildHeadlessRuntimeProofSection(
  bundle: StudioHeadlessAuthorityBundle,
): WorkingReportModel["sections"][number] {
  return {
    title: "0. Headless runtime proof",
    body: "Assembly, story coverage, overlap audit, browser runtime E2E, app play video proof를 같은 source-strict bundle로 묶어 표시합니다.",
    rows: [
      {
        label: "Application assembly",
        value: formatStatusDetail(
          bundle.application?.status,
          bundle.application?.readiness,
        ),
      },
      {
        label: "Story coverage",
        value: bundle.storyCoverage?.status ?? "not reported",
      },
      {
        label: "Overlap audit",
        value: bundle.overlapAudit?.status ?? "not reported",
      },
      {
        label: "Runtime E2E",
        value: formatStatusDetail(
          bundle.runtimeE2E?.status,
          bundle.runtimeE2E?.finalSurfaceRef,
        ),
      },
      {
        label: "Video proof",
        value: formatStatusDetail(
          bundle.videoProof?.status,
          bundle.videoProof?.videoRef,
        ),
      },
      {
        label: "Final text",
        value:
          bundle.runtimeE2E?.finalText ??
          bundle.videoProof?.finalText ??
          "not reported",
      },
      {
        label: "Account UX boundary",
        value: formatAccountUxBoundary(bundle.videoProof?.accountUxNotMounted),
      },
      {
        label: "Source requirement gaps",
        value: formatAuthorityGapCount(bundle),
      },
    ],
  };
}

function collectReportSourceFrames(input: {
  sourceConversion: Record<string, unknown> | undefined;
  pages: Record<string, unknown>[];
  figmaImport: Record<string, unknown> | undefined;
  sourceAnalysisReport: Record<string, unknown> | undefined;
}): Array<{ name: string; detail: string }> {
  const reportFrames = readArray(input.sourceAnalysisReport?.frames)
    ?.map((frame) => readRecord(frame))
    .filter((frame): frame is Record<string, unknown> => frame != null)
    .map((frame) => ({
      name:
        readString(frame.frameName) ?? readString(frame.name) ?? "Figma frame",
      detail:
        readString(frame.sourceFrameLabel) ??
        readString(frame.sourceFramePlatform) ??
        "source-derived",
    }));
  if (reportFrames && reportFrames.length > 0) return reportFrames;

  const records: Record<string, unknown>[] = [];
  const directSourceFrame = readRecord(input.sourceConversion?.sourceFrame);
  if (directSourceFrame) records.push(directSourceFrame);
  for (const frame of readArray(input.sourceConversion?.sourceFrames) ?? []) {
    const record = readRecord(frame);
    if (record) records.push(record);
  }
  for (const page of input.pages) {
    const record = readRecord(page.sourceFrame);
    if (record) records.push(record);
  }
  const metadataSourceFrame = readRecord(input.figmaImport?.sourceFrame);
  if (metadataSourceFrame) records.push(metadataSourceFrame);

  const seen = new Set<string>();
  return records
    .map((record) => {
      const width = readNumber(record.width);
      const height = readNumber(record.height);
      const platform = readString(record.platform);
      const name =
        readString(record.frameName) ??
        readString(record.name) ??
        readString(record.id) ??
        "Figma frame";
      const detail =
        width && height
          ? `${width}x${height}px${platform ? ` · ${platform}` : ""}`
          : (platform ?? "source-derived");
      return { name, detail };
    })
    .filter((frame) => {
      const key = `${frame.name}:${frame.detail}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function readArray(value: unknown): unknown[] | undefined {
  return Array.isArray(value) ? value : undefined;
}

function readStringList(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const strings = value.filter(
    (item): item is string => typeof item === "string" && item.trim() !== "",
  );
  return strings.length > 0 ? strings : undefined;
}

function countArray(value: unknown[] | undefined): number | undefined {
  return value ? value.length : undefined;
}

function countRecord(
  value: Record<string, unknown> | undefined,
): number | undefined {
  return value ? Object.keys(value).length : undefined;
}

function formatCount(value: number | undefined): string {
  return value === undefined ? "대기" : String(value);
}

function formatNodeSummary(
  nodeCount: number | undefined,
  textNodeCount: number | undefined,
): string {
  if (nodeCount === undefined) return "대기";
  if (textNodeCount === undefined) return `${nodeCount} nodes`;
  return `${nodeCount} nodes / ${textNodeCount} text`;
}

function formatMatchedSummary(
  matchedNodes: number | undefined,
  nodeCount: number | undefined,
): string {
  if (matchedNodes === undefined) return "대기";
  return nodeCount === undefined
    ? String(matchedNodes)
    : `${matchedNodes}/${nodeCount}`;
}

function formatCoverageSummary(
  matchedNodes: number | undefined,
  nodeCount: number | undefined,
): string {
  if (matchedNodes === undefined || nodeCount === undefined || nodeCount <= 0) {
    return "대기";
  }
  return `${Math.round((matchedNodes / nodeCount) * 100)}%`;
}

function formatTestDetail(
  tests: Record<string, unknown>[] | undefined,
  name: string,
): string {
  const test = tests?.find((candidate) => readString(candidate.name) === name);
  if (!test) return "자동 테스트 후 기록";
  const status = readString(test.status) ?? "unknown";
  const detail = readString(test.detail);
  return detail ? `${status} · ${detail}` : status;
}

function formatReasonSummary(reasons: string[] | undefined): string {
  if (!reasons || reasons.length === 0) return "사용자 visual review 필요";
  return reasons.slice(0, 2).join(" / ");
}

function resolveEditablePageId(
  project: unknown,
  currentPageId: string | null | undefined,
  editorApp: unknown,
): string | null {
  const manifest = readRecord(readRecord(project)?.manifest);
  const pages = Array.isArray(manifest?.pages)
    ? manifest.pages.map(readRecord).filter((page): page is Record<string, unknown> => page != null)
    : [];
  const active = pages.find((page) => readString(page.id) === currentPageId) ?? pages[0];
  const activeId = readString(active?.id);
  const editorPages = readRecord(editorApp)?.pages;
  return activeId && Array.isArray(editorPages) && editorPages.some(
    (page) => readString(readRecord(page)?.id) === activeId,
  )
    ? activeId
    : null;
}

function readRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === "object"
    ? (value as Record<string, unknown>)
    : undefined;
}

function readString(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

function readNumber(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value)
    ? value
    : undefined;
}

function WorkingArtifactRail({
  workingView,
  reportKind,
  onReportKindChange,
}: {
  workingView: "app" | "report";
  reportKind: WorkingReportKind;
  onReportKindChange: (kind: WorkingReportKind) => void;
}) {
  return (
    <div
      className="flex h-full min-h-0 flex-col bg-card/60"
      data-testid="working-artifact-rail"
    >
      {workingView === "report" ? (
        <DocumentListRail
          reportKind={reportKind}
          onReportKindChange={onReportKindChange}
        />
      ) : (
        <PreviewPageListRail />
      )}
    </div>
  );
}

function DocumentListRail({
  reportKind,
  onReportKindChange,
}: {
  reportKind: WorkingReportKind;
  onReportKindChange: (kind: WorkingReportKind) => void;
}) {
  const { strings } = useStudioLocale();
  const copy = strings.ide;
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="border-b border-border px-3 py-2">
        <div className="text-xs font-semibold text-foreground">
          {copy.documents.title}
        </div>
        <div className="mt-0.5 text-[11px] text-muted-foreground">
          {copy.documents.count(REPORT_KIND_OPTIONS.length)}
        </div>
      </div>
      <div
        className="min-h-0 flex-1 space-y-2 overflow-auto p-2"
        data-testid="document-navigation-rail"
      >
        {REPORT_KIND_OPTIONS.map((option) => {
          const selected = option.id === reportKind;
          return (
            <button
              key={option.id}
              type="button"
              data-testid={`document-navigation-item-${option.id}`}
              aria-pressed={selected}
              onClick={() => onReportKindChange(option.id)}
              className={[
                "w-full rounded-md border px-2.5 py-2 text-left transition-colors",
                selected
                  ? "border-primary/40 bg-primary/5 text-foreground"
                  : "border-transparent text-muted-foreground hover:border-border hover:bg-background hover:text-foreground",
              ].join(" ")}
            >
              <div className="text-xs font-medium">{option.label}</div>
              <div className="mt-1 flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
                <span>{copy.device.a4Portrait}</span>
                <span>{copy.documents.pageCount}</span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function PreviewPageListRail() {
  const { strings } = useStudioLocale();
  const copy = strings.ide.pages;
  const projectPages = useStudioStore((s) => s.currentProject?.manifest?.pages);
  const currentPageId = useStudioStore((s) => s.currentPageId);
  const setCurrentPageId = useStudioStore((s) => s.setCurrentPageId);
  const chatContext = useStudioStore((s) => s.chatContext);
  const setChatContext = useStudioStore((s) => s.setChatContext);
  const editorApp = useEditorStore((s) => s.app);
  const editorActivePageId = useEditorStore((s) => s.activePageId);

  const pages = useMemo(() => {
    const rawPages =
      Array.isArray(projectPages) && projectPages.length > 0
        ? projectPages
        : (editorApp?.pages ?? []);

    return rawPages
      .map((page) => {
        const record = page as unknown as Record<string, unknown>;
        const id = typeof record.id === "string" ? record.id : "";
        if (!id) return null;
        return {
          id,
          label: String(record.title ?? record.name ?? record.id),
          route: typeof record.route === "string" ? record.route : id,
        };
      })
      .filter(
        (page): page is { id: string; label: string; route: string } =>
          page != null,
      );
  }, [editorApp?.pages, projectPages]);

  const activePageId = pages.some((page) => page.id === currentPageId)
    ? currentPageId
    : pages.some((page) => page.id === editorActivePageId)
      ? editorActivePageId
      : pages[0]?.id;
  const firstPage = pages[0];
  const selectPage = (pageId: string) => {
    const page = pages.find((candidate) => candidate.id === pageId);
    setCurrentPageId(pageId);
    setChatContext({
      ...chatContext,
      surfaceId: "preview",
      pageId,
      pageName: page?.label,
      selectionSurface: "page",
    });
    if (editorApp?.pages.some((item) => item.id === pageId)) {
      useEditorStore.getState().setActivePage(pageId);
    }
  };
  const openRunnablePreview = () => {
    if (firstPage) selectPage(firstPage.id);
  };
  const runnablePreviewSelected = pages.length === 0;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="border-b border-border px-3 py-2">
        <div className="text-xs font-semibold text-foreground">
          {copy.title}
        </div>
        <div className="mt-0.5 text-[11px] text-muted-foreground">
          {pages.length > 0
            ? copy.summaryWithPages(pages.length)
            : copy.summaryPreviewOnly}
        </div>
      </div>
      <div
        className="min-h-0 flex-1 space-y-2 overflow-auto p-2"
        data-testid="preview-page-list"
      >
        <button
          type="button"
          data-testid="preview-page-navigation-item-app-preview"
          data-page-kind="preview"
          aria-pressed={runnablePreviewSelected}
          onClick={openRunnablePreview}
          className={[
            "w-full rounded-md border px-2.5 py-2 text-left transition-colors",
            runnablePreviewSelected
              ? "border-primary/40 bg-primary/5 text-foreground"
              : "border-border bg-background text-foreground hover:bg-muted",
          ].join(" ")}
        >
          <div className="flex items-center justify-between gap-2">
            <span className="truncate text-xs font-semibold">
              {copy.runnableTitle}
            </span>
            <span className="text-[11px] text-muted-foreground">
              {copy.runnableBadge}
            </span>
          </div>
          <div className="mt-1 truncate text-[11px] text-muted-foreground">
            {copy.runnableDetail}
          </div>
        </button>
        {pages.length === 0 ? (
          <div
            className="rounded-md border border-border bg-background px-2.5 py-2 text-xs text-muted-foreground"
            data-testid="preview-page-navigation-empty"
          >
            {copy.emptyTitle}
            <div className="mt-1 text-[11px]">{copy.emptyBody}</div>
          </div>
        ) : (
          pages.map((page, index) => {
            const selected = page.id === activePageId;
            return (
              <button
                key={page.id}
                type="button"
                data-testid={`preview-page-navigation-item-${page.id}`}
                data-page-kind="page"
                aria-pressed={selected}
                onClick={() => selectPage(page.id)}
                className={[
                  "w-full rounded-md border px-2.5 py-2 text-left transition-colors",
                  selected
                    ? "border-primary/40 bg-primary/5 text-foreground"
                    : "border-transparent text-muted-foreground hover:border-border hover:bg-background hover:text-foreground",
                ].join(" ")}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-xs font-medium">
                    {page.label}
                  </span>
                  <span className="text-[11px] text-muted-foreground">
                    {index + 1}
                  </span>
                </div>
                <div className="mt-1 truncate text-[11px] text-muted-foreground">
                  {page.route}
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
